import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { baseAPI } from '../../../config';
import { expedientStatusLabel } from '@claimup/shared/constants/expedient';

const EVENT_LABELS = {
  processing_started: 'Inicio de procesamiento',
  processing_completed: 'Procesamiento completado',
  processing_error: 'Error de procesamiento',
  rate_limited: 'Limitado por tasa',
  max_retries_reached: 'Maximo de reintentos',
  circuit_breaker_open: 'Circuit breaker abierto',
  ai_called: 'Llamada IA',
  ai_responded: 'Respuesta IA',
  action_executed: 'Accion ejecutada',
  context_gathered: 'Contexto recopilado',
  prompt_resolved: 'Prompt resuelto',
  follow_up_created: 'Follow-up creado',
  sequence_stopped: 'Secuencia parada',
};

const EVENT_COLORS = {
  processing_started: 'border-slate-400 bg-slate-400',
  processing_completed: 'border-green-500 bg-green-500',
  processing_error: 'border-red-500 bg-red-500',
  rate_limited: 'border-yellow-500 bg-yellow-500',
  max_retries_reached: 'border-red-500 bg-red-500',
  circuit_breaker_open: 'border-red-500 bg-red-500',
  ai_called: 'border-cyan-500 bg-cyan-500',
  ai_responded: 'border-blue-500 bg-blue-500',
  action_executed: 'border-indigo-500 bg-indigo-500',
  context_gathered: 'border-slate-500 bg-slate-500',
  prompt_resolved: 'border-purple-500 bg-purple-500',
};

const ROLE_BADGE_COLORS = {
  system: 'bg-purple-100 text-purple-700',
  user: 'bg-slate-200 text-slate-700',
  assistant: 'bg-blue-100 text-blue-700',
  tool: 'bg-amber-100 text-amber-700',
};

const Section = ({ title, children }) => (
  <div className='mt-2'>
    <div className='text-[10px] uppercase font-semibold text-slate-500 mb-1'>{title}</div>
    {children}
  </div>
);

const Pre = ({ children, className = '' }) => (
  <pre className={`text-xs text-slate-700 bg-slate-50 rounded-lg p-2 whitespace-pre-wrap break-words ${className}`}>
    {children}
  </pre>
);

const JsonBlock = ({ value }) => (
  <pre className='text-xs text-slate-600 bg-slate-50 rounded-lg p-2 whitespace-pre-wrap break-words max-h-96 overflow-auto'>
    {JSON.stringify(value, null, 2)}
  </pre>
);

const RoleBadge = ({ role }) => (
  <span
    className={`inline-flex px-2 py-0.5 text-[10px] font-semibold uppercase rounded-full ${
      ROLE_BADGE_COLORS[role] || 'bg-slate-100 text-slate-600'
    }`}
  >
    {role}
  </span>
);

const ToolCallBlock = ({ toolCall }) => {
  const name = toolCall?.function?.name || toolCall?.name;
  const args = toolCall?.function?.arguments ?? toolCall?.arguments;
  let parsedArgs = args;
  if (typeof args === 'string') {
    try {
      parsedArgs = JSON.parse(args);
    } catch {
      parsedArgs = args;
    }
  }
  return (
    <div className='text-xs bg-white border border-slate-200 rounded-lg p-2'>
      <div className='flex items-center gap-2 flex-wrap'>
        <span className='font-mono font-semibold text-slate-800'>{name}</span>
        {toolCall?.id && <span className='text-[10px] text-slate-400'>{toolCall.id}</span>}
      </div>
      {parsedArgs !== undefined && parsedArgs !== null && parsedArgs !== '' && (
        <pre className='whitespace-pre-wrap break-words mt-1 text-slate-700 bg-slate-50 rounded-lg p-1.5'>
          {typeof parsedArgs === 'string' ? parsedArgs : JSON.stringify(parsedArgs, null, 2)}
        </pre>
      )}
    </div>
  );
};

const MessagesList = ({ messages }) => (
  <div className='space-y-2'>
    {messages.map((m, i) => (
      <div key={i} className='border border-slate-200 rounded-lg p-2 bg-white'>
        <div className='flex items-center justify-between mb-1 gap-2'>
          <RoleBadge role={m.role} />
          {m.tool_call_id && (
            <span className='text-[10px] text-slate-400 font-mono truncate'>{m.tool_call_id}</span>
          )}
        </div>
        {m.content !== undefined && m.content !== null && m.content !== '' && (
          <pre className='text-xs whitespace-pre-wrap break-words text-slate-700'>
            {typeof m.content === 'string' ? m.content : JSON.stringify(m.content, null, 2)}
          </pre>
        )}
        {Array.isArray(m.tool_calls) && m.tool_calls.length > 0 && (
          <div className='mt-2 space-y-1'>
            {m.tool_calls.map((tc, j) => (
              <ToolCallBlock key={j} toolCall={tc} />
            ))}
          </div>
        )}
      </div>
    ))}
  </div>
);

const formatDuration = (ms) => (ms != null ? `${(ms / 1000).toFixed(2)}s` : null);

const renderEventBody = (log) => {
  const d = log.details || {};

  switch (log.event) {
    case 'processing_started':
      return d.assignment ? (
        <Section title='Snapshot del assignment'>
          <JsonBlock value={d.assignment} />
        </Section>
      ) : null;

    case 'prompt_resolved':
      return (
        <>
          <div className='text-xs text-slate-500 mt-0.5'>
            {log.ai_provider && <span className='font-medium'>{log.ai_provider}</span>}
            {log.model_id && <> / <span className='font-medium'>{log.model_id}</span></>}
            {log.prompt_version && <> · v{log.prompt_version}</>}
            {d.max_tokens && <> · max_tokens: {d.max_tokens}</>}
          </div>
          {d.prompt_text && (
            <Section title='Prompt'>
              <Pre className='max-h-64 overflow-auto'>{d.prompt_text}</Pre>
            </Section>
          )}
          {Array.isArray(d.tools_enabled) && d.tools_enabled.length > 0 && (
            <Section title='Tools habilitadas'>
              <div className='flex flex-wrap gap-1'>
                {d.tools_enabled.map((t) => (
                  <span
                    key={t}
                    className='text-[10px] px-2 py-0.5 bg-slate-100 text-slate-700 rounded-full font-mono'
                  >
                    {t}
                  </span>
                ))}
              </div>
            </Section>
          )}
          {d.context_config && (
            <Section title='Configuracion de contexto'>
              <JsonBlock value={d.context_config} />
            </Section>
          )}
        </>
      );

    case 'context_gathered':
      return (
        <>
          <div className='grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-1 text-xs text-slate-600 mt-1'>
            <div>Interacciones: <span className='font-semibold'>{d.interactions_count ?? 0}</span></div>
            <div>Otros assignments: <span className='font-semibold'>{d.other_assignments_count ?? 0}</span></div>
            <div>Anotaciones: <span className='font-semibold'>{d.annotations_count ?? 0}</span></div>
            <div>Documentos: <span className='font-semibold'>{d.documents_count ?? 0}</span></div>
            {d.debtor_phone && <div>Tel: {d.debtor_phone}</div>}
            {d.debtor_email && <div className='truncate' title={d.debtor_email}>Email: {d.debtor_email}</div>}
            {d.expedient_status && <div>Estado expediente: {expedientStatusLabel(d.expedient_status)}</div>}
          </div>
          {d.formatted_context && (
            <Section title='Contexto enviado al modelo'>
              <Pre className='max-h-72 overflow-auto'>{d.formatted_context}</Pre>
            </Section>
          )}
        </>
      );

    case 'ai_called':
      return (
        <>
          <div className='text-xs text-slate-500 mt-0.5'>
            Ronda {d.round_number} · {d.message_count ?? (d.messages ? d.messages.length : 0)} mensajes
            {log.ai_provider && <> · {log.ai_provider}/{log.model_id}</>}
          </div>
          {Array.isArray(d.messages) && d.messages.length > 0 && (
            <Section title='Mensajes enviados'>
              <MessagesList messages={d.messages} />
            </Section>
          )}
          {Array.isArray(d.tools) && d.tools.length > 0 && (
            <Section title={`Tools (${d.tools.length})`}>
              <div className='flex flex-wrap gap-1'>
                {d.tools.map((t, i) => (
                  <span
                    key={i}
                    className='text-[10px] px-2 py-0.5 bg-slate-100 text-slate-700 rounded-full font-mono'
                  >
                    {t?.function?.name || t?.name || `tool_${i}`}
                  </span>
                ))}
              </div>
            </Section>
          )}
        </>
      );

    case 'ai_responded':
      return (
        <>
          <div className='text-xs text-slate-500 mt-0.5'>
            Ronda {d.round_number}
            {d.stop_reason && <> · stop: {d.stop_reason}</>}
            {d.tool_calls_count != null && <> · {d.tool_calls_count} tool calls</>}
            {log.tokens_used != null && <> · {log.tokens_used} tokens</>}
            {formatDuration(log.duration_ms) && <> · {formatDuration(log.duration_ms)}</>}
          </div>
          {d.content !== undefined && d.content !== null && d.content !== '' && (
            <Section title='Respuesta'>
              <Pre className='max-h-64 overflow-auto'>
                {typeof d.content === 'string' ? d.content : JSON.stringify(d.content, null, 2)}
              </Pre>
            </Section>
          )}
          {Array.isArray(d.tool_calls) && d.tool_calls.length > 0 && (
            <Section title='Tool calls'>
              <div className='space-y-1'>
                {d.tool_calls.map((tc, i) => (
                  <ToolCallBlock key={i} toolCall={tc} />
                ))}
              </div>
            </Section>
          )}
        </>
      );

    case 'action_executed':
      return (
        <>
          <div className='text-xs text-slate-500 mt-0.5'>
            <span className='font-mono font-semibold text-slate-800'>{d.tool}</span>
            {d.round_number && <> · ronda {d.round_number}</>}
            {formatDuration(log.duration_ms) && <> · {formatDuration(log.duration_ms)}</>}
            {d.result && (
              <>
                {' · '}
                {d.result.executed ? (
                  <span className='text-green-600 font-medium'>OK</span>
                ) : (
                  <span className='text-red-600 font-medium'>fallido</span>
                )}
              </>
            )}
          </div>
          {d.arguments && (
            <Section title='Argumentos'>
              <JsonBlock value={d.arguments} />
            </Section>
          )}
          {d.result && (
            <Section title='Resultado'>
              <JsonBlock value={d.result} />
            </Section>
          )}
        </>
      );

    case 'processing_error':
      return (
        <>
          {d.reason && (
            <div className='text-xs text-red-600 font-semibold mt-0.5'>{d.reason}</div>
          )}
          {d.error && <div className='text-xs text-red-600'>{d.error}</div>}
          {d.tool && (
            <div className='text-xs text-slate-500'>
              Tool: <span className='font-mono'>{d.tool}</span>
              {d.tool_call_id && <span className='text-slate-400'> · {d.tool_call_id}</span>}
            </div>
          )}
          {d.validation_error && (
            <div className='text-xs text-red-600'>Validacion: {d.validation_error}</div>
          )}
          {d.round_number && (
            <div className='text-xs text-slate-500'>Ronda {d.round_number}</div>
          )}
          {d.arguments && (
            <Section title='Argumentos'>
              <JsonBlock value={d.arguments} />
            </Section>
          )}
          {Array.isArray(d.messages) && d.messages.length > 0 && (
            <Section title='Mensajes en el momento del error'>
              <MessagesList messages={d.messages} />
            </Section>
          )}
        </>
      );

    case 'processing_completed':
      return (
        <>
          <div className='text-xs text-slate-500 mt-0.5'>
            {d.rounds_count != null && <>Rondas: {d.rounds_count}</>}
            {d.total_tokens_used != null && <> · {d.total_tokens_used} tokens</>}
            {log.duration_ms != null && <> · {formatDuration(log.duration_ms)}</>}
          </div>
          {d.cancelled_reason && (
            <div className='text-xs text-yellow-700 mt-1'>Cancelado: {d.cancelled_reason}</div>
          )}
          {d.eligibility && (
            <Section title='Eligibilidad'>
              <JsonBlock value={d.eligibility} />
            </Section>
          )}
          {Array.isArray(d.final_messages) && d.final_messages.length > 0 && (
            <Section title={`Mensajes finales (${d.final_messages.length})`}>
              <MessagesList messages={d.final_messages} />
            </Section>
          )}
        </>
      );

    default:
      return Object.keys(d).length > 0 ? <JsonBlock value={d} /> : null;
  }
};

const LogItem = ({ log }) => {
  const [showRaw, setShowRaw] = useState(false);
  const dotColor = EVENT_COLORS[log.event] || 'border-slate-400 bg-slate-400';
  const hasRawDetails = log.details && Object.keys(log.details).length > 0;

  const formatTime = (dateStr) =>
    new Date(dateStr).toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });

  return (
    <div className='relative pl-8'>
      <div className={`absolute left-1.5 top-1.5 w-3 h-3 rounded-full ${dotColor}`}></div>

      <div>
        <div className='flex items-center gap-2 flex-wrap'>
          <span className='text-sm font-medium text-slate-800'>
            {EVENT_LABELS[log.event] || log.event}
          </span>
          <span className='text-xs text-slate-400'>{formatTime(log.createdAt)}</span>
          {hasRawDetails && (
            <button
              onClick={() => setShowRaw(s => !s)}
              className='ml-auto text-[10px] text-slate-400 hover:text-slate-600 underline'
            >
              {showRaw ? 'Ocultar JSON' : 'Ver JSON'}
            </button>
          )}
        </div>

        {renderEventBody(log)}

        {showRaw && hasRawDetails && (
          <Section title='JSON crudo'>
            <JsonBlock value={log.details} />
          </Section>
        )}
      </div>
    </div>
  );
};

const AIAgentLogBatchDetail = ({ batchId, modalOpen, onClose }) => {
  const [logs, setLogs] = useState([]);
  const [assignment, setAssignment] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!modalOpen || !batchId) return;
    setLoading(true);

    const fetchBatch = async () => {
      try {
        const response = await axios.get(`${baseAPI}/ai-agent-logs/batch/${batchId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        const data = response.data;
        if (Array.isArray(data)) {
          setLogs(data);
          setAssignment(null);
        } else {
          setLogs(data.logs || []);
          setAssignment(data.assignment || null);
        }
      } catch (error) {
        console.error('Error fetching batch detail:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBatch();
  }, [batchId, modalOpen]);

  // Close on ESC
  useEffect(() => {
    if (!modalOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [modalOpen, onClose]);

  if (!modalOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className='fixed inset-0 bg-gray-900 bg-opacity-30 z-50' onClick={onClose} />

      {/* Modal */}
      <div
        className='fixed inset-0 z-50 overflow-hidden flex items-center justify-center px-4'
        onClick={onClose}
      >
        <div
          className='bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-auto'
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className='px-5 py-3 border-b border-gray-200 flex justify-between items-center'>
            <div className='font-semibold text-gray-800'>Detalle de ejecucion</div>
            <button className='text-gray-400 hover:text-gray-500' onClick={onClose}>
              <svg className='w-4 h-4 fill-current'>
                <path d='M7.95 6.536l4.242-4.243a1 1 0 111.415 1.414L9.364 7.95l4.243 4.242a1 1 0 11-1.415 1.415L7.95 9.364l-4.243 4.243a1 1 0 01-1.414-1.415L6.536 7.95 2.293 3.707a1 1 0 011.414-1.414L7.95 6.536z' />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className='px-5 py-4'>
            <div className='text-xs text-slate-400 mb-2'>Batch: {batchId}</div>

            {assignment && (
              <div className='mb-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2'>
                <div className='text-xs uppercase font-semibold text-slate-500 mb-1'>Assignment trigger</div>
                <div className='text-sm text-slate-800 font-medium'>{assignment.description}</div>
                <div className='text-xs text-slate-500 mt-1 flex flex-wrap gap-x-3 gap-y-0.5'>
                  {assignment.task_type && (
                    <span>
                      <span className='font-semibold'>Tipo:</span> {assignment.task_type}
                    </span>
                  )}
                  {assignment.priority && (
                    <span>
                      <span className='font-semibold'>Prioridad:</span> {assignment.priority}
                    </span>
                  )}
                  {assignment.status && (
                    <span>
                      <span className='font-semibold'>Estado:</span> {assignment.status}
                    </span>
                  )}
                  {assignment.date && (
                    <span>
                      <span className='font-semibold'>Fecha:</span>{' '}
                      {new Date(assignment.date).toLocaleString('es-ES', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  )}
                  {assignment.expedient_id && (
                    <a
                      href={`/view/${assignment.expedient_id}`}
                      className='text-blue-600 hover:underline'
                    >
                      Expediente {assignment.expedient_id.toString().slice(-6)}
                    </a>
                  )}
                  <span className='text-slate-400'>id: {assignment._id}</span>
                </div>
              </div>
            )}

            {loading ? (
              <div className='text-sm text-slate-500'>Cargando...</div>
            ) : logs.length === 0 ? (
              <div className='text-sm text-slate-400'>Sin logs para este batch</div>
            ) : (
              <div className='relative'>
                {/* Timeline line */}
                <div className='absolute left-3 top-2 bottom-2 w-0.5 bg-slate-200'></div>

                <div className='space-y-4'>
                  {logs.map((log) => (
                    <LogItem key={log._id} log={log} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AIAgentLogBatchDetail;
